import { inject, Injectable } from '@angular/core';
import { SERVICE_POINT_PROFILES, ServicePointProfile } from '../models/service-point.model';
import { CreateAgentDTO, User, UserWithPii } from '../models/user.model';

import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { Pii } from '../models/pii.model';
import { DatabaseService } from './base/database.service';
import { getBrowserFingerprint } from './fingerprint';
import { QueueService } from './queue.service';
import { ServicePointService } from './service-point.service';

/**
 * Request para /profile endpoint - centraliza updates em cp_user e cp_pii
 */
export interface DuplicateUsersCheckResult {
  isDuplicateClaim: boolean;
  hasDuplicateWithKycPassed: boolean;
  /** Outros usuários com o mesmo documento, SEM o usuário atual (currentUserId já excluído).
   *  Use este campo para qualquer UI que liste "os duplicados" (badge, popup, etc). */
  duplicateUsers: UserWithPii[];
  /** TODOS os usuários com o mesmo documento, INCLUINDO o usuário atual.
   *  Não usar diretamente para listar "duplicados" em UI — vai mostrar o próprio
   *  usuário como se fosse um duplicado (CPK-4365: bug replicado do Agent Home
   *  pro CP Firm porque essa lista foi usada por engano no popup). */
  allUsersWithSameDocument: UserWithPii[];
}

export interface UpdateProfileRequest {
  user?: Partial<User>;
  pii?: Partial<Pii>;
  user_id?: string; // Para ADMIN/AGENT modificar outro usuário
  invalidateKYC?: boolean; // Forçar invalidação de KYC
  bypassKYC?: boolean; // ADMIN pode ignorar proteção de KYC
}

/**
 * Response padronizada do /profile endpoint
 */
export interface UpdateProfileResponse {
  success: boolean;
  user?: User;
  pii?: Pii;
  errors?: string[];
  warnings?: string[]; // Ex: "KYC invalidated", "Sensitive fields ignored"
}

@Injectable({
  providedIn: 'root',
})
export class UserService extends DatabaseService<User> {
  UserAccessList: { [resource: string]: Role[] } = {
    'page/firm': ['ADMINISTRATOR'],
    'page/firm/home': ['ADMINISTRATOR'],
    'page/firm/agents': ['ADMINISTRATOR'],
    'page/firm/agents/:id': ['ADMINISTRATOR'],
    'page/firm/agent-dashboard': ['ADMINISTRATOR', 'AGENT'],
    'page/firm/upload-claim-documents': ['ADMINISTRATOR', 'AGENT'],
    'page/firm/clients': ['ADMINISTRATOR'],
    'page/firm/clients/:id': ['ADMINISTRATOR', 'FUNDER'],
    'page/firm/clients/:id/profile': ['ADMINISTRATOR'],
    // AGENT reaches this page from the kanban's KYC-review card, which is the whole point of
    // the kyc_case queue. Read + "Review case" only: the page hides every other action for
    // them (see kyc.component.ts isAgent).
    'page/firm/clients/:id/kyc': ['ADMINISTRATOR', 'FUNDER', 'AGENT'],
    'page/firm/thread/:id': ['ADMINISTRATOR'],
    'page/firm/cases': ['ADMINISTRATOR'],
    'page/firm/messages': ['ADMINISTRATOR', 'AGENT'],
    'page/firm/news': ['ADMINISTRATOR'],
    'page/firm/news/:news': ['ADMINISTRATOR'],
    'page/firm/faqs': ['ADMINISTRATOR'],
    'page/firm/faqs/new': ['ADMINISTRATOR'],
    'page/firm/faqs/:id': ['ADMINISTRATOR'],
    'page/firm/case/:id': ['ADMINISTRATOR'],
    'page/firm/case/:id/:tab': ['ADMINISTRATOR'],
    'page/firm/case/:id/qb/:qbid': ['ADMINISTRATOR'],
    'page/firm/claim/:id': ['ADMINISTRATOR'],
    'page/firm/cases/new': ['ADMINISTRATOR'],
    'page/firm/profile': ['ADMINISTRATOR', 'CO-COUNSEL'],
    'page/firm/cocounsel/claims': ['ADMINISTRATOR', 'CO-COUNSEL'],
    'page/firm/cocounsel/claims/v1': ['ADMINISTRATOR', 'CO-COUNSEL'],
    'page/firm/cocounsel/dashboard': ['CO-COUNSEL'],
    'page/firm/funder/dashboard': ['ADMINISTRATOR', 'FUNDER'],
    'page/firm/metabase': ['ADMINISTRATOR'],
    'page/firm/settings': ['ADMINISTRATOR'],
    // Agendamento presencial (CPK-4922). AGENT com perfil entra por ServicePointAccessList.
    'page/firm/agendamentos': ['ADMINISTRATOR'],
    'page/firm/agendamentos/agenda': ['ADMINISTRATOR'],
    'page/firm/agendamentos/novo': ['ADMINISTRATOR'],
    'page/firm/agendamentos/campanhas': ['ADMINISTRATOR'],
    'page/firm/agendamentos/campanhas/nova': ['ADMINISTRATOR'],
    'page/firm/agendamentos/campanhas/:id': ['ADMINISTRATOR'],
    'page/firm/agendamentos/guiches': ['ADMINISTRATOR'],
    'page/firm/agendamentos/perfis': ['ADMINISTRATOR'],
    'page/firm/agendamentos/atendimento/triagem': ['ADMINISTRATOR'],
    'page/firm/agendamentos/atendimento/guiche': ['ADMINISTRATOR'],
  };

  ServicePointAccessList: { [resource: string]: ServicePointProfile[] } = {
    'page/firm/agendamentos': [...SERVICE_POINT_PROFILES],
    'page/firm/agendamentos/agenda': [...SERVICE_POINT_PROFILES],
    'page/firm/agendamentos/novo': ['AGENDAMENTO'],
    'page/firm/agendamentos/perfis': ['SUPERVISOR'],
    'page/firm/agendamentos/atendimento/triagem': ['TRIAGEM'],
    'page/firm/agendamentos/atendimento/guiche': ['GUICHE'],
  };

  private servicePoints = inject(ServicePointService);
  private queue = inject(QueueService);

  constructor(private router: Router, private http: HttpClient) {
    super('cp_user');
  }

  private cachedUser: User | null = null;

  async getWithPII(id: string) {
    const u = await this.supabase
      .from('cp_user')
      .select('*,pii:cp_pii(*)')
      .eq('id', id);

    // const u2 = await this.supabase
    //   .from('cp_user')
    //   .select('id, display_name')
    //   .limit(20);

    // console.log('JM user', u);
    // console.log('JM user single', u2);
    const ret: any = { ...u.data![0] };

    if (ret.pii && ret.pii[0]) {
      ret.pii = ret.pii[0];
    }

    return ret as unknown as UserWithPii;
  }

  async getUserListByDocumentValue(document_value: string) {
    const u = await this.supabase
      .from('cp_pii')
      .select('*,cp_user:cp_user(*)')
      .ilike('id_document_value', `%${document_value}%`);

    if (u.error) {
      return null;
    }

    const list = u.data!.map((e) => {
      const ret: any = { pii: e, ...e.cp_user };
      delete ret.pii.cp_user;

      return ret as unknown as UserWithPii;
    });

    return list as unknown as UserWithPii[];
  }

async getUserListByDocumentValueWithKyc(document_value: string) {
  const { data, error } = await this.supabase
    .from('cp_pii')
    .select(`
      *,
      cp_user!inner(
        *,
        cp_kyc_check(*)
      )
    `)
    .ilike('id_document_value', `%${document_value}%`);

  if (error) {
    console.error('[getUserListByDocumentValueWithKyc] Error:', error);
    return null;
  }

  const list = data!.map((e: any) => {
    const user = Array.isArray(e.cp_user) ? e.cp_user[0] : e.cp_user;

    return {
      pii: e,
      ...user,
      kyc_checks: user?.cp_kyc_check || []
    };
  });

  return list;
}

async checkDuplicateUsers(
  documentValue: string,
  currentUserId?: string
): Promise<DuplicateUsersCheckResult> {
  const empty: DuplicateUsersCheckResult = {
    isDuplicateClaim: false,
    hasDuplicateWithKycPassed: false,
    duplicateUsers: [],
    allUsersWithSameDocument: [],
  };

  // Sem currentUserId não há como excluir o próprio usuário da comparação —
  // continuar aqui marcaria SEMPRE isDuplicateClaim=true (o próprio registro
  // bateria como "outro usuário"), um falso positivo silencioso pra todo cliente.
  if (!documentValue || !currentUserId) return empty;

  try {
    const usersWithKyc = await this.getUserListByDocumentValueWithKyc(documentValue);
    if (!usersWithKyc || usersWithKyc.length === 0) return empty;

    const otherUsers = usersWithKyc.filter((u: any) => u.pii?.user_id !== currentUserId);
    if (otherUsers.length === 0) {
      return { ...empty, allUsersWithSameDocument: usersWithKyc };
    }

    const hasKycPassed = otherUsers.some((u: any) =>
      u.kyc_checks?.some((check: any) => check.stage === 'DONE' && check.passed)
    );

    return {
      isDuplicateClaim: true,
      hasDuplicateWithKycPassed: hasKycPassed,
      duplicateUsers: otherUsers,
      allUsersWithSameDocument: usersWithKyc,
    };
  } catch (error) {
    console.error('[checkDuplicateUsers] Error:', error);
    return empty;
  }
}




  async getUserByDocumentValue(document_value: string) {
    const u = await this.supabase
      .from('cp_pii')
      .select('*,cp_user:cp_user(*)')
      .textSearch('search_vector2', document_value);
    if (u.error) {
      return null;
    }
    const ret: any = { pii: u.data![0], ...u.data![0].cp_user };
    delete ret.pii.cp_user;

    return ret as unknown as UserWithPii;
  }

  /**
   * Busca usuários por nome, user_id, claim_id, email ou telefone.
   * @param searchstring termo de busca
   */
  async searchUsers(searchstring: string, limit?: number): Promise<UserWithPii[] | null> {
    console.log('[user.service.ts] SearchUsers', searchstring);

    // Monta filtros textuais
    const orFilters = [
      `legal_name.ilike.%${searchstring}%`,
      `id_document_value.ilike.%${searchstring}%`,
      `contacts->>phone.ilike.%${searchstring}%`,
      `contacts->>email.ilike.%${searchstring}%`,
    ];

    // Se for UUID, adiciona filtro por user_id
    const isUUID = /^[0-9a-fA-F-]{36}$/.test(searchstring);
    if (isUUID) {
      orFilters.push(`user_id.eq.${searchstring}`);
    }

    let query = this.supabase
      .from('cp_pii')
      .select(
        '*,cp_user:cp_user(*),cp_claim_permission:cp_user(cp_claim_permission(*,claim_id))'
      )
      .or(orFilters.join(','));

    // Se for claim_id, buscar usuários relacionados ao claim
    if (isUUID) {
      const claimUsers = await this.supabase
        .from('cp_claim_permission')
        .select('principal_id')
        .eq('claim_id', searchstring);
      if (claimUsers.data && claimUsers.data.length > 0) {
        const userIds = claimUsers.data.map((c: any) => c.principal_id);
        query = this.supabase
          .from('cp_pii')
          .select(
            '*,cp_user:cp_user(*),cp_claim_permission:cp_user(cp_claim_permission(*,claim_id))'
          )
          .in('user_id', userIds);
      }
    }

    if(limit){
      query = query.limit(limit);
    }

    const u = await query;
    if (u.error) {
      return null;
    }

    const usrs = u.data.map((d) => {
      const ret: any = { pii: d, ...d.cp_user };
      delete ret.cp_user;
      delete ret.pii.cp_user;
      return ret as unknown as UserWithPii;
    });

    return usrs;
  }
  async listUsersClientByPartialNameWithClaims(
    partial: string = '',
    limit: number = 5
  ) {
    console.log('JM listUsersClientByPartialNameWithClaims', partial, limit);
    const query = this.supabase
      .from('cp_user')
      .select(
        `
        id,
        display_name,
        cp_claim_permission!inner(claim_id, cp_claim(*, cp_case(*)))`
      )
      .eq('primary_role', 'CLIENT')
      .ilike('display_name', `%${partial}%`)
      .limit(limit);

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching users with claims:', error.message);
      return [];
    }

    // Remove usuários duplicados (caso tenham várias claims)
    const uniqueUsers = Array.from(
      new Map(data.map((user: any) => [user.id, user])).values()
    );

    return uniqueUsers as unknown as User[];
  }

  async verifyEmail(email: string, token: string) {
    return this.supabase.auth.verifyOtp({ email, token, type: 'invite' });
  }

  async verifyEmailWithTokenHash(token_hash: string) {
    return this.supabase.auth.verifyOtp({ token_hash, type: 'invite' });
  }

  async createSessionWithRedirect(access_token: string, refresh_token: string){
    // IMPORTANTE: Limpar sessão anterior, cache e localStorage para evitar:
    // 1. Erro "Invalid Refresh Token" do auto-refresh de sessão expirada
    // 2. Race condition onde o cachedUser antigo é usado ao invés do novo usuário
    console.log('[USER SERVICE] [createSessionWithRedirect] Clearing previous session and cache');

    // Limpa sessão anterior do Supabase (evita auto-refresh com token inválido)
    await this.supabase.auth.signOut({ scope: 'local' });

    // Limpa cache local
    this.cachedUser = null;
    localStorage.removeItem('userRole');

    const ret = await this.supabase.auth.setSession({
      access_token: access_token,
      refresh_token: refresh_token,
    });

    console.log('[USER SERVICE] [createSessionWithRedirect] Session set result:', {
      hasSession: !!ret.data.session,
      userId: ret.data.session?.user?.id,
      error: ret.error?.message
    });

    if(ret.data.session){
      return ret.data.session;
    }else{
      console.error('Error: ', ret.error);
    }

    return ret;
  }

  /**
   * Cria sessão usando o access_token e opcionalmente o refresh_token.
   * Útil para links de email com token de curta duração.
   * Se refresh_token não for fornecido, a sessão NÃO poderá ser renovada automaticamente quando expirar.
   */
  async createSessionWithAccessTokenOnly(access_token: string, refresh_token?: string) {
    console.log('[USER SERVICE] [createSessionWithAccessTokenOnly] Creating session', {
      hasRefreshToken: !!refresh_token
    });

    // Limpa sessão anterior
    await this.supabase.auth.signOut({ scope: 'local' });
    this.cachedUser = null;
    localStorage.removeItem('userRole');

    // Usa refresh_token se fornecido, senão string vazia (sessão não renovável)
    const ret = await this.supabase.auth.setSession({
      access_token: access_token,
      refresh_token: refresh_token || '',
    });

    const s = await this.supabase.auth.getSession()

    console.log('[USER SERVICE] [createSessionWithAccessTokenOnly] Current session after setSession:', ret)
    console.log('[USER SERVICE] [createSessionWithAccessTokenOnly] getSession result:', s)

    console.log('[USER SERVICE] [createSessionWithAccessTokenOnly] Session set result:', {
      hasSession: !!ret.data.session,
      userId: ret.data.session?.user?.id,
      expiresAt: ret.data.session?.expires_at,
      error: ret.error?.message
    });

    if (ret.data.session) {
      return { session: ret.data.session, error: null };
    } else {
      console.error('[USER SERVICE] [createSessionWithAccessTokenOnly] Error:', ret.error);
      return { session: null, error: ret.error };
    }
  }

  async signInWithMagicLink(token_hash: string) {
    localStorage.removeItem('userRole');

    const ret = await this.supabase.auth.verifyOtp({
      token_hash,
      type: 'magiclink',
    });

    if (ret.data.session) {
      this.supabase.auth.setSession(ret.data.session);
    } else {
      console.error('Invalid OTP');
    }

    return ret;
  }

  async forceSBSignup(email: string, password: string) {
    localStorage.removeItem('userRole');

    const { data, error } = await this.supabase.auth.signUp({
      email,
      password,
    });

    if (error) throw new Error(error.message);
  }

  async signUp(email: string, password: string, profile: User) {
    localStorage.removeItem('userRole');

    const req = {
      case_id: environment.case_id,
      display_name: profile,
      email: email,
      password: password,
      phone: null,
      affected: true,
      agreetoterms: true,
    };

    const token = (await this.supabase.auth.getSession()).data.session
      ?.access_token;

    const ret = (await firstValueFrom(
      this.http.post(
        environment.n8n_url + '/webhook/cp/eligibility.create',
        { detail: { data: req } },
        {
          headers: {
            apikey: token ?? '',
            Authorization: 'Bearer ' + (token ?? ''),
          },
        }
      )
    )) as any;

    this.router.navigateByUrl('/auth/login');
    return;
  }

  async upsertAgent(createAgent: CreateAgentDTO) {
    const token = (await this.supabase.auth.getSession()).data.session
      ?.access_token;

    const ret = (await firstValueFrom(
      this.http.put(environment.lambdaUrl + '/upsert-agent', createAgent, {
        headers: {
          Authorization: 'Bearer ' + (token ?? ''),
        },
      })
    )) as any;

    return ret;
  }

  async getSupervisors() {
    const { data, error } = await this.supabase
      .from('cp_user')
      .select('id, display_name, primary_email, primary_role')
      .in('primary_role', ['ADMINISTRATOR', 'AGENT'])
      .order('display_name');

    if (error) throw new Error(error.message);
    return data as User[];
  }

  async signIn(email: string, password: string, persist: boolean = true) {
    // Clear all localStorage and session data to prevent cross-user contamination
    localStorage.removeItem('userRole');
    this.cachedUser = null; // Clear cached user as well

    const signin = await this.supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (signin.data.user) {
      await this.supabase.auth.setSession({
        access_token: signin.data.session?.access_token,
        refresh_token: signin.data.session?.refresh_token,
      });
      console.log('[USER SERVICE] [SIGN IN] signin');
      // const a = await this.supabase.auth.refreshSession({
      //   refresh_token: signin.data.session?.refresh_token,
      // });
      // console.log('[USER SERVICE] [SIGN IN] a', a);
      // if (a.error) {
      //   throw new Error(a.error.message);
      // }

      if (persist) {
        await this.supabase.auth.startAutoRefresh();
      }
      console.log('[USER SERVICE] [SIGN IN] a2');
      // await this.supabase.auth.setSession()
    }

    const cur = await this.getCurrentUser();
    // console.log('[USER SERVICE] [SIGN IN] CUR', cur);
    setTimeout(async () => {
      const cur = await this.getCurrentUser();
      // console.log('[USER SERVICE] [SIGN IN] 200 CUR', cur);
    }, 200);

    return signin;
  }

  async refresh() {
    const { data, error } = await this.supabase.auth.refreshSession();

    if (error) {
      console.error('Error refreshing session:', error.message);
      return null;
    }

    return data;
  }

  async signInAgent(email: string, password: string, supervisorAgent: string) {
    localStorage.removeItem('userRole');

    // TODO: implement lambda here
    const signin: any = {};

    if (signin.data.user) {
      await this.supabase.auth.setSession({
        access_token: signin.data.session?.access_token,
        refresh_token: signin.data.session?.refresh_token,
      });
    }

    const cur = await this.getCurrentUser();
    console.log('[USER SERVICE] [SIGN IN] CUR', cur);
    setTimeout(async () => {
      const cur = await this.getCurrentUser();
      console.log('[USER SERVICE] [SIGN IN] 200 CUR', cur);
    }, 200);

    return signin;
  }

  async signOut() {
    await this.queue.closeSessionOnSignOut(this.cachedUser?.id ?? null);

    try {
      const { error } = await this.supabase.auth.signOut();
      if (error) {
        console.warn('[USER SERVICE] supabase signOut error:', error.message);
      }
    } catch (e) {
      console.warn('[USER SERVICE] supabase signOut threw:', e);
    }

    console.log('Signing out');

    this.clearLocalSession();

    this.router.navigate(['/auth/login']);
  }

  clearLocalSession() {
    this.cachedUser = null;
    localStorage.removeItem('userRole');
    localStorage.removeItem('userProfile');
    sessionStorage.clear();
  }

  async resetPassword(email: string) {
    return await this.supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${environment.projectUrl}auth/reset?`,
    });
  }

  async getSessionWithPasswordResetToken(token: string) {
    const a = await this.supabase.auth.verifyOtp({
      type: 'recovery',
      token_hash: token,
    });

    if (a.error) {
      throw a.error;
    }
    return a.data;
  }

  async updatePassword(password: string) {
    return await this.supabase.auth.updateUser({ password });
  }

  /**
   * Método centralizado para atualizar perfil do usuário via /profile endpoint.
   * Todas as operações de UPDATE em cp_user e cp_pii devem usar este método.
   *
   * Benefícios:
   * - Validação automática de KYC
   * - Sync automático com AWS Connect
   * - Validação de permissões por ROLE (CLIENT/AGENT/ADMIN)
   * - Logs estruturados
   * - Respostas padronizadas com warnings
   *
   * @param request Dados para atualizar (user, pii, user_id para ADMIN/AGENT)
   * @returns Resposta padronizada com dados atualizados e avisos
   */
  async updateProfile(
    request: UpdateProfileRequest
  ): Promise<UpdateProfileResponse> {
    const token = (await this.supabase.auth.getSession()).data.session
      ?.access_token;

    try {
      const response = await firstValueFrom(
        this.http.post<UpdateProfileResponse>(
          `${environment.lambdaUrl}/profile`,
          request,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        )
      );

      return response;
    } catch (error: any) {
      // Retorna erro padronizado preservando a resposta do backend (retError → { message,
      // details: { error_code } }), para que chamadores possam tratar códigos específicos
      // (ex: IDENTITY_FRAUD_DETECTED no fluxo de correção do KYC).
      const errorMsg =
        error?.error?.message ||
        error?.error?.error ||
        error?.message ||
        'Erro ao atualizar perfil';
      const err = new Error(errorMsg) as Error & {
        status?: number;
        errorCode?: string;
      };
      err.status = error?.status;
      err.errorCode = error?.error?.details?.error_code;
      throw err;
    }
  }

  /**
   * @override DatabaseService.update()
   * Delega para updateProfile() com validações automáticas de KYC e permissões
   */
  override async update(t: Partial<User>) {
    // Delega para updateProfile() com validações automáticas
    const response = await this.updateProfile({ user: t });

    if (!response.success || !response.user) {
      throw new Error(
        response.errors?.join(', ') || 'Erro ao atualizar usuário'
      );
    }

    // Retorna array para manter compatibilidade com código existente (DatabaseService)
    return [response.user];
  }

  async updatePii(pii: Partial<Pii>) {
    delete pii.search_vector;
    delete (pii as any).search_vector2;

    // Delega para updateProfile() com validações automáticas
    const response = await this.updateProfile({ pii });

    if (!response.success || !response.pii) {
      throw new Error(response.errors?.join(', ') || 'Erro ao atualizar PII');
    }

    // Retorna array para manter compatibilidade com código existente
    return [response.pii];
  }

  async sendMagicLinkEmail(email: string) {
    localStorage.removeItem('userRole');

    return await this.supabase.auth.signInWithOtp({ email, options: { shouldCreateUser: false } });
  }

  /** Resends both channels for an expired automation link (CPK-4180) via the
   *  email-only mode of sms-send-otp: a new magic-link email always, plus an SMS
   *  OTP when the account has a phone on file. Debounced server-side (60s), so
   *  this is safe to call on every mount/click without a client-side guard.
   *  Returns the phone the SMS was sent to, or null if there's none on file. */
  async resendExpiredLink(email: string): Promise<string | null> {
    const res: any = await firstValueFrom(
      this.http.post(`${environment.lambdaUrl}/sms-send-otp`, { email })
    );
    return res?.details?.phone ?? null;
  }

  async sendSmsOtp(phone: string, email?: string): Promise<void> {
    localStorage.removeItem('userRole');

    const session = await this.supabase.auth.getSession();
    const token = session.data.session?.access_token;

    await firstValueFrom(
      this.http.post(
        `${environment.lambdaUrl}/sms-send-otp`,
        { phone, ...(email ? { email } : {}) },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      )
    );
  }

  async verifySmsOtp(phone: string, token: string) {
    const normalizedPhone = phone.startsWith('+') ? phone.slice(1) : phone;
    return this.supabase.auth.verifyOtp({ phone: normalizedPhone, token, type: 'sms' });
  }

  async sendManuallyMagicLink(email: string) {
    const session = await this.supabase.auth.getSession();
    const token = session.data.session?.access_token;

    return firstValueFrom(
      this.http.post(
        environment.lambdaUrl + '/send-magic-link-email',
        { email },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )
    );
  }

  async sendAccessLinkToClient(userId: string, channel: 'email' | 'sms'): Promise<void> {
    const session = await this.supabase.auth.getSession();
    const token = session.data.session?.access_token;

    await firstValueFrom(
      this.http.post(
        `${environment.lambdaUrl}/send-access-link`,
        { user_id: userId, channel },
        { headers: { Authorization: `Bearer ${token}` } }
      )
    );
  }

  async sendAgentOtpToClient(userId: string, channel: 'email' | 'sms'): Promise<void> {
    const session = await this.supabase.auth.getSession();
    const token = session.data.session?.access_token;

    await firstValueFrom(
      this.http.post(
        `${environment.lambdaUrl}/send-agent-otp`,
        { user_id: userId, channel },
        { headers: { Authorization: `Bearer ${token}` } }
      )
    );
  }

  async verifyAgentOtp(userId: string, code: string): Promise<void> {
    const session = await this.supabase.auth.getSession();
    const token = session.data.session?.access_token;

    await firstValueFrom(
      this.http.post(
        `${environment.lambdaUrl}/verify-agent-otp`,
        { user_id: userId, code },
        { headers: { Authorization: `Bearer ${token}` } }
      )
    );
  }

  async notifyPasswordChanged(): Promise<void> {
    const session = await this.supabase.auth.getSession();
    const token = session.data.session?.access_token;

    await firstValueFrom(
      this.http.post(
        `${environment.lambdaUrl}/notify-password-changed`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      )
    );
  }

  async updateUser(param: any, pii?: any, invalidateKYC?: boolean) {
    try {
      // Delega para updateProfile() com validações automáticas
      const response = await this.updateProfile({ user: param, pii, invalidateKYC });

      // Atualiza cache e mantém compatibilidade
      this.cachedUser = response.user!;
      return response.user;
    } catch (error) {
      throw error;
    }
  }

  clearUserCache(): void {
    this.cachedUser = null;
  }

  async getCurrentUser(): Promise<User | null> {
    if (this.cachedUser) {
      console.log('[USER SERVICE] [getCurrentUser] Returning cached user:', {
        id: this.cachedUser.id,
        auth_id: this.cachedUser.auth_id,
        display_name: this.cachedUser.display_name
      });
      return this.cachedUser;
    }

    const au = (await this.supabase.auth.getSession()).data.session?.user;
    console.log('[USER SERVICE] [getCurrentUser] Session user (auth):', { auth_id: au?.id });
    if (!au || !au.id) return null;

    const { data, error } = await this.supabase
      .from('cp_user')
      .select('*')
      .eq('auth_id', au.id);

    if (error || !data || data.length === 0) {
      console.log('[USER SERVICE] [getCurrentUser] No cp_user found for auth_id:', au.id);
      return null;
    }

    console.log('[USER SERVICE] [getCurrentUser] Found cp_user:', {
      id: data[0].id,
      auth_id: data[0].auth_id,
      display_name: data[0].display_name
    });

    this.cachedUser = data[0] as unknown as User;
    return this.cachedUser;
  }

  /**
   * Aguarda até que o usuário atual seja carregado ou o timeout seja atingido.
   * Útil para evitar race conditions após login/criação de conta.
   * @param maxAttempts Número máximo de tentativas (default: 10)
   * @param intervalMs Intervalo entre tentativas em ms (default: 500)
   */
  async waitForCurrentUser(maxAttempts: number = 10, intervalMs: number = 500): Promise<User | null> {
    let user = await this.getCurrentUser();
    let attempts = 0;

    while (!user && attempts < maxAttempts) {
      // console.log(`[USER SERVICE] Waiting for user... attempt ${attempts + 1}`);
      await new Promise(resolve => setTimeout(resolve, intervalMs));
      user = await this.getCurrentUser();
      attempts++;
    }

    return user;
  }

  async getCurrentUserRole(): Promise<any | null> {
    const au = await this.supabase.auth.getUser();
    // console.log('AU', au);
    if (!au || !au.data.user || !au.data.user.id) return null;

    const userquery = (
      await this.supabase
        .from('cp_user')
        .select('id, primary_role, attributes')
        .eq('auth_id', au.data.user?.id)
    ).data;

    if (!userquery || userquery.length == 0) return null;

    return userquery[0] as unknown as User;
  }

  getFingerprint() {
    return getBrowserFingerprint();
  }

  async tryToLogin(token: string, rt: string) {
    localStorage.removeItem('userRole');

    // console.log('Trying to login', token, rt);
    await this.supabase.auth.setSession({
      access_token: token,
      refresh_token: rt,
    });

    await this.supabase.auth.refreshSession({
      refresh_token: rt,
    });

    const usr = await this.supabase.auth.getUser();
    // console.log('USR', usr);
    return !!usr.data.user;
  }

  // MFA Methods (Multi-Factor Authentication) Start

  /**
   *
   * @returns A list of available MFA (Multi-Factor Authentication) factors with yours stats
   */
  listMFAEnrolls() {
    return this.supabase.auth.mfa.listFactors();
  }

  /**
   *
   * @returns A configurable factor for MFA (Multi-Factor Authentication), with ID and QR Code
   */
  async initMFAEnroll() {
    console.log('Init MFA Enroll');
    const au = await this.supabase.auth.getUser();
    // console.log('AU', au);
    const { data, error } = await this.supabase.auth.mfa.enroll({
      factorType: 'totp',
      issuer: `Client-Portal`,
    });

    if (error) throw new Error(error.message);
    if (!data) throw new Error('No data returned');

    // console.log('MFA Enroll Data: ', data);
    return {
      id: data.id,
      qrcode: data.totp.qr_code,
      secret: data.totp.secret,
    };
  }

  /**
   *
   * @param factorId The ID of the factor inited with initMFAEnroll
   * @returns The ID of challenge to use on verify() supabase method
   */
  async challengeMFAEnroll(factorId: string) {
    const { data, error } = await this.supabase.auth.mfa.challenge({
      factorId,
    });

    if (error) throw new Error(error.message);
    if (!data) throw new Error('No data returned');

    return data.id;
  }

  /**
   *
   * @param factorId factorId to be configured
   * @param challengeId challenge of the factorId configuration
   * @param token totp token to be verified
   * @returns true if the token is valid
   */
  async verifyMFAEnroll(factorId: string, challengeId: string, token: string) {
    const { data, error } = await this.supabase.auth.mfa.verify({
      factorId,
      challengeId,
      code: token,
    });

    // console.log('Verify MFA Enroll Data: ', data);

    if (error) throw new Error(error.message);

    return true;
  }

  /**
   *
   * @param factorId factorId to be removed
   * @returns true if the factor was removed
   */
  async removeFactorMFAEnroll(factorId: string) {
    const { data, error } = await this.supabase.auth.mfa.unenroll({
      factorId,
    });

    if (error) throw new Error(error.message);

    return true;
  }

  /**
   *
   * @param factor factor to be verified
   * @param token totp token to be verified
   * @returns true if the token is valid
   */
  async verifyMFACode(factor: string, token: string) {
    try {
      const { data, error } = await this.supabase.auth.mfa.challengeAndVerify({
        factorId: factor,
        code: token,
      });

      if (error) throw error;
      if (!data) throw new Error('No data returned');
      if (data) {
        this.supabase.auth.setSession({
          access_token: data.access_token,
          refresh_token: data.refresh_token,
        });
      }

      // console.log('Verify Password Reset Token Data: ', data);
      return data;
    } catch (error: any) {
      throw new Error(error.message);
    }
  }

  // MFA Methods (Multi-Factor Authentication) End

  // Reset Password Methods Start

  /**
   *
   * @param email Email to send the reset password link
   * @returns A message if the email was sent
   */
  async sendResetPasswordEmail(email: string) {
    const redirecturl = `${environment.projectUrl}auth/reset?email=${email}`;
    // console.log('Redirect URL: ', redirecturl);
    return await this.supabase.auth.resetPasswordForEmail(email, {
      redirectTo: redirecturl,
    });
  }

  /**
   *
   * @param email Email to send the reset password link
   * @param token Token to verify the email
   * @returns A message if the email was sent
   */
  async verifyPasswordResetToken(email: string, token: string) {
    const { data, error } = await this.supabase.auth.verifyOtp({
      email,
      type: 'email',
      token,
    });

    if (error) throw new Error(error.message);
    if (!data) throw new Error('No data returned');

    // console.log('Verify Password Reset Token Data: ', data);
    return data;
  }

  /**
   *
   * @param email Email to send the reset password link
   * @param code Code challenge to verify the email
   * @returns A message if the email was sent
   */
  async verifyPasswordResetCodeChallenger(email: string, code: string) {
    const { data, error } = await this.supabase.auth.verifyOtp({
      email,
      type: 'recovery',
      token: code,
    });

    if (error) throw new Error(error.message);
    if (!data) throw new Error('No data returned');

    // console.log('Verify Password Reset Code Challenger Data: ', data);
    return data;
  }
  // Reset Password Methods End

  // Account Recovery by CPF Methods Start (CPK-4585 / CPK-3824 Fluxo 1)

  /**
   * Requests account recovery by CPF + email + phone — used when the client
   * doesn't remember (or can't reach) the exact email their account is under.
   * The backend never reveals whether the CPF matched anything; always
   * resolves with the same generic message.
   */
  async requestAccountRecoveryByCpf(
    cpf: string,
    email: string,
    phone: string,
    captchaToken: string
  ) {
    return firstValueFrom(
      this.http.post<{ message: string }>(
        `${environment.lambdaUrl}/account-recovery`,
        {
          cpf,
          email,
          phone,
          captcha_token: captchaToken,
          // CPK-4685: the lambda picks the Turnstile secret paired with this sitekey
          captcha_sitekey: environment.turnstileSiteKey,
        }
      )
    );
  }

  /**
   * Verifies the OTP sent by requestAccountRecoveryByCpf. On success, the
   * backend hands back the account's real registered email plus a one-time
   * magiclink token — completed here via the normal Supabase verifyOtp, same
   * as every other magic-link login in this app.
   */
  async verifyAccountRecoveryOtp(
    cpf: string,
    email: string,
    phone: string,
    otpCode: string
  ) {
    const response = await firstValueFrom(
      this.http.post<{ message: string; details: { email: string; token: string } }>(
        `${environment.lambdaUrl}/account-recovery-verify`,
        { cpf, email, phone, otp_code: otpCode }
      )
    );

    const { data: sessionData, error } = await this.supabase.auth.verifyOtp({
      email: response.details.email,
      token: response.details.token,
      type: 'magiclink',
    });

    if (error) throw new Error(error.message);
    if (!sessionData) throw new Error('No data returned');

    return sessionData;
  }

  // Account Recovery by CPF Methods End

  async updateProfileAndPII(user: User) {}

  async logInWithOTP(email: string, token: string) {
    return await this.supabase.auth.verifyOtp({
      email,
      token,
      type: 'magiclink',
    });
  }

  async hasAccess(resource: string) {
    const user = await this.getCurrentUser();
    const role = user?.primary_role;

    if (!user || !role) return false;

    if (!this.UserAccessList[resource]) {
      console.error('Resource not registered', resource);
      return false;
    }

    if (this.UserAccessList[resource].findIndex((r) => r == role) !== -1) return true;

    const profiles = this.ServicePointAccessList[resource];
    if (role !== 'AGENT' || !profiles || !user.id) return false;

    return this.servicePoints.hasAnyValidProfile(user.id, profiles);
  }

  async authAgent(
    email: string,
    password: string,
    supervisorEmail: string,
    supervisorPw: string
  ) {
    console.log('[User service] - Auth agent');
    const a = (await firstValueFrom(
      this.http.post(environment.lambdaUrl + '/auth/agent', {
        user_agent: email,
        password_agent: password,
        user_supervisor: supervisorEmail,
        password_supervisor: supervisorPw,
      })
    )) as AgentAuthLambdaRet;

    console.log('[User service] - Auth agent ret', a);

    const r = await this.tryToLogin(a.data.access_token, a.data.refresh_token);

    return r;
  }

  /**
   * Post-login verification for non-CLIENT/non-ADMINISTRATOR users.
   * Calls the auth-agent lambda in verify-only mode (Authorization header with the current session token).
   * Returns:
   *   - { verified: true,  requiresSupervisor: false } → user passes all restrictions
   *   - { verified: false, requiresSupervisor: true  } → supervisor required, redirect to /auth/login/agent
   * Throws an error (with message from lambda) when the agent is inactive, out of schedule, or IP-blocked.
   */
  async verifyAgentPostLogin(): Promise<{ verified: boolean; requiresSupervisor: boolean }> {
    const session = (await this.supabase.auth.getSession()).data.session;
    if (!session?.access_token) {
      throw new Error('No active session');
    }

    const result = await firstValueFrom(
      this.http.post<{ verified: boolean; requiresSupervisor: boolean }>(
        environment.lambdaUrl + '/auth/agent',
        {},
        { headers: { Authorization: `Bearer ${session.access_token}` } }
      )
    );

    return result;
  }

  async createUserWithoutClaim(userData: {
    display_name: string;
    email: string;
    document_type: string;
    id_document_value: string;
    phone: string;
    date_of_birth: string;
  }) {
    const token = (await this.supabase.auth.getSession()).data.session
      ?.access_token;
    const ret = await firstValueFrom(
      this.http.post(
        environment.lambdaUrl + '/create_user_without_claim',
        {
          display_name: userData.display_name,
          email: userData.email,
          document_type: userData.document_type,
          id_document_value: userData.id_document_value,
          phone: userData.phone,
          date_of_birth: userData.date_of_birth,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )
    );

    return ret as { message: string; data: string };
  }

  async createUserWithClaim(userData: {
    case_id: string;
    display_name: string;
    /** Pelo menos um entre email e phone deve ser fornecido. null quando ausente. */
    email: string | null;
    phone?: string | null;
    default_language?: string;
    document_type?: string;
    id_document_value?: string;
    date_of_birth?: string;
    captcha_token?: string;
    origination?: {
      button?: string | null;
      url_params?: Record<string, any> | null;
    } | null;
  }) {
    const ret = await firstValueFrom(
      this.http.post(
        environment.lambdaUrl + '/create_user_and_claim',
        userData,
        {
          headers: {
            apikey: environment.supabaseKey,
          },
        }
      )
    );

    return ret as any;
  }
}

// export interface UserAndPII {
//   id uuid not null default gen_random_uuid (),
//   created_at timestamp with time zone null default now(),
//   display_name text null,
//   primary_email text null,
//   language text null,
//   avatar_url text null,
//   best_time_to_call time with time zone null,
//   primary_auth_type text null,
//   primary_auth_value text null,
//   primary_role text null,
//   attributes jsonb null,
//   auth_id uuid null,

//     created_at timestamp with time zone null default now(),
//     date_of_birth date null,
//     legal_name text null,
//     id_document_type text null,
//     id_document_value text null,
//     attributes jsonb null,
//     primary_address jsonb null,
//     contacts jsonb null,
//     documents jsonb null,
//     user_id uuid null,
//     id uuid not null default gen_random_uuid (),

// }

interface AgentAuthLambdaRet {
  data: {
    access_token: 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJhdWQiOiJhdXRoZW50aWNhdGVkIiwiZXhwIjoxNzQwMTc5NjE4LCJpYXQiOjE3NDAxNzYwMTgsInN1YiI6IjMyZTgyMDZjLTExMmYtNGQ4Zi1hODdkLTUzNTBlYTM0ZDM2ZCIsImVtYWlsIjoibGVhbmRyby5hZ2VudG8xNEBzb21vcy51cyIsInBob25lIjoiIiwiYXBwX21ldGFkYXRhIjp7InByb3ZpZGVyIjoiZW1haWwiLCJwcm92aWRlcnMiOlsiZW1haWwiXX0sInVzZXJfbWV0YWRhdGEiOnt9LCJyb2xlIjoiYXV0aGVudGljYXRlZCIsImFhbCI6ImFhbDEiLCJhbXIiOlt7Im1ldGhvZCI6InBhc3N3b3JkIiwidGltZXN0YW1wIjoxNzQwMTc2MDE4fV0sInNlc3Npb25faWQiOiI0MzYxZTMxMS01NmU2LTRjZTktYjhlYy1jOGMzMzZkYzI2ZTIifQ.xvDc_PztLi3hsttLwHKqkJwZmLFlDKGEef8df93qu8k';
    refresh_token: 'RjHQOIYIT874hxZx54dFHw';
    user: {
      id: '32e8206c-112f-4d8f-a87d-5350ea34d36d';
      aud: 'authenticated';
      role: 'authenticated';
      email: 'leandro.agento14@somos.us';
      email_confirmed_at: '2025-01-28T18:05:43.957264Z';
      phone: '';
      confirmed_at: '2025-01-28T18:05:43.957264Z';
      last_sign_in_at: '2025-02-21T22:13:38.01102329Z';
      app_metadata: { provider: 'email'; providers: ['email'] };
      user_metadata: {};
      identities: [
        {
          id: '32e8206c-112f-4d8f-a87d-5350ea34d36d';
          user_id: '32e8206c-112f-4d8f-a87d-5350ea34d36d';
          identity_data: {
            email: 'leandro.agento14@somos.us';
            sub: '32e8206c-112f-4d8f-a87d-5350ea34d36d';
          };
          provider: 'email';
          last_sign_in_at: '2025-01-28T18:05:43.950609Z';
          created_at: '2025-01-28T18:05:43.950667Z';
          updated_at: '2025-01-28T18:05:43.950667Z';
        }
      ];
      created_at: '2025-01-28T18:05:43.945327Z';
      updated_at: '2025-02-21T22:13:38.014508Z';
    };
  };
}

interface UserAccess {
  role: Role;
}

export type Role = 'CO-COUNSEL' | 'AGENT' | 'ADMINISTRATOR' | 'CLIENT' | 'FUNDER';
